import { create } from 'zustand';

// ── Types ──────────────────────────────────────────────────
export type CycleStatus =
  | 'draft'
  | 'published'
  | 'active'
  | 'in-progress'
  | 'under-review'
  | 'hr-review'
  | 'completed'
  | 'closed'
  | 'archived';

export interface AppraisalCycle {
  id: string;
  name: string;
  description: string;
  year: number;
  status: CycleStatus;
  startDate: string;
  endDate: string;
  employeeWindowStart: string;
  employeeWindowEnd: string;
  managerWindowStart: string;
  managerWindowEnd: string;
  hrWindowStart: string;
  hrWindowEnd: string;
  totalAssigned: number;
  submitted: number;
  managerReviewed: number;
  hrReviewed: number;
  completed: number;
  department: string;
  scoringScale: number;
  approvalLevels: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  templateId: string;
  templateName: string;
  autoCloseEnabled: boolean;
  freezeOnSubmit: boolean;
  sequentialApproval: boolean;
}

export type QuestionType =
  | 'rating'
  | 'text'
  | 'textarea'
  | 'single-select'
  | 'multi-select'
  | 'goal'
  | 'kpi'
  | 'competency'
  | 'dropdown'
  | 'radio'
  | 'checkbox'
  | 'conditional';

export interface Question {
  id: string;
  text: string;
  type: QuestionType;
  required: boolean;
  weightage: number;
  maxChars?: number;
  options?: string[];
  minRating?: number;
  maxRating?: number;
  conditionalParentId?: string;
  conditionalValue?: string;
  sectionId: string;
  order: number;
}

export interface TemplateSection {
  id: string;
  name: string;
  description: string;
  order: number;
  weightage: number;
  questions: Question[];
}

export interface AppraisalTemplate {
  id: string;
  name: string;
  description: string;
  version: string;
  status: 'draft' | 'active' | 'archived';
  sections: TemplateSection[];
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  usedInCycles: number;
}

export interface ActivityItem {
  id: string;
  type: 'submit' | 'review' | 'approve' | 'reject' | 'create' | 'close' | 'overdue';
  actor: string;
  action: string;
  target: string;
  timestamp: string;
  cycleId?: string;
}

// ── Store ──────────────────────────────────────────────────
interface AppraisalStore {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;

  cycles: AppraisalCycle[];
  setCycles: (c: AppraisalCycle[]) => void;
  addCycle: (c: AppraisalCycle) => void;
  updateCycle: (id: string, updates: Partial<AppraisalCycle>) => void;
  deleteCycle: (id: string) => void;

  templates: AppraisalTemplate[];
  setTemplates: (t: AppraisalTemplate[]) => void;
  addTemplate: (t: AppraisalTemplate) => void;
  updateTemplate: (id: string, updates: Partial<AppraisalTemplate>) => void;

  activities: ActivityItem[];

  selectedCycleIds: string[];
  setSelectedCycleIds: (ids: string[]) => void;
  toggleCycleSelection: (id: string) => void;
  clearCycleSelection: () => void;

  activeTemplateId: string | null;
  setActiveTemplateId: (id: string | null) => void;
}

export const useAppraisalStore = create<AppraisalStore>((set) => ({
  sidebarCollapsed: false,
  setSidebarCollapsed: (v) => set({ sidebarCollapsed: v }),

  cycles: [],
  setCycles: (cycles) => set({ cycles }),
  addCycle: (c) => set((s) => ({ cycles: [c, ...s.cycles] })),
  updateCycle: (id, updates) =>
    set((s) => ({ cycles: s.cycles.map((c) => (c.id === id ? { ...c, ...updates } : c)) })),
  deleteCycle: (id) => set((s) => ({ cycles: s.cycles.filter((c) => c.id !== id) })),

  templates: [],
  setTemplates: (templates) => set({ templates }),
  addTemplate: (t) => set((s) => ({ templates: [t, ...s.templates] })),
  updateTemplate: (id, updates) =>
    set((s) => ({ templates: s.templates.map((t) => (t.id === id ? { ...t, ...updates } : t)) })),

  activities: [],

  selectedCycleIds: [],
  setSelectedCycleIds: (ids) => set({ selectedCycleIds: ids }),
  toggleCycleSelection: (id) =>
    set((s) => ({
      selectedCycleIds: s.selectedCycleIds.includes(id)
        ? s.selectedCycleIds.filter((x) => x !== id)
        : [...s.selectedCycleIds, id],
    })),
  clearCycleSelection: () => set({ selectedCycleIds: [] }),

  activeTemplateId: null,
  setActiveTemplateId: (id) => set({ activeTemplateId: id }),
}));
